import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Video, Brain, Trash2, Lock, Bot, Users, Handshake, Sparkles } from "lucide-react";
import Footer from "@/components/Footer";

export default function Trust() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 flex flex-col" data-testid="trust-screen">
      <header className="sticky top-0 z-10 border-b border-neutral-800 bg-[#0a0a0a]/95 backdrop-blur px-4 py-3 flex items-center justify-between"
              style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}>
        <Link to="/" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="trust-back">
          <ArrowLeft size={16} /> Gully
        </Link>
        <div className="text-sm">Trust & Privacy</div>
        <div className="w-12"></div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-5 py-8 space-y-6 w-full">
        <div>
          <p className="text-xs uppercase tracking-widest text-emerald-400/80 mb-2">Why Trust Gully?</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" data-testid="trust-hero-title">
            Trust matters more than growth.
          </h1>
          <p className="mt-3 text-[15px] text-neutral-400 leading-relaxed">
            Gully is built on the belief that a player&apos;s conversations, videos and reflections belong to them.
            Partners help us reach more cricketers — never at the cost of a player&apos;s privacy.
          </p>
        </div>

        {/* "Why Trust Gully?" — philosophy, not legal text. */}
        <section className="rounded-2xl border border-emerald-800/50 bg-emerald-950/30 p-5" data-testid="trust-why-block">
          <div className="flex items-center gap-2 text-sm text-emerald-300 mb-3">
            <Sparkles size={16} /> <span className="font-medium">Our philosophy</span>
          </div>
          <ul className="space-y-2.5 text-[15px] text-neutral-100 leading-relaxed">
            <li className="flex gap-2"><span className="text-emerald-400">•</span> Your conversations are private.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> Coaches see trends, not private messages.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> We succeed only when you succeed.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> We don&apos;t charge partners to join.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> We earn only when players choose Gully Plus.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> We believe trust is more important than growth.</li>
          </ul>
        </section>

        <Block icon={Users} title="Player privacy — what coaches never see" testid="trust-block-player-privacy">
          Coaches and academies you&apos;re connected to on Gully <strong>cannot</strong> read your conversations,
          read your reports, watch your videos, or identify quiet players. Every partner-facing insight is either
          about the partner&apos;s public page or aggregated across at least 5 members.
        </Block>

        <Block icon={Handshake} title="Team Pulse — K-anonymity in plain English" testid="trust-block-team-pulse">
          When 5 or more players in an academy actively use Gully, we may show the coach a
          Team Pulse — an <em>anonymised</em> view of engagement. If any signal could identify an
          individual (fewer than 5 players contributing), we hide it. That&apos;s K-anonymity — the
          smallest group we ever report on is 5.
        </Block>

        <Block icon={Bot} title="How AI is used" testid="trust-block-ai">
          Gully is an AI cricket companion — no human is reading your messages or watching your clips.
          On the partner side, our Marketing Studio can generate <strong>drafts</strong> of captions,
          reels, and WhatsApp messages, but Gully <strong>never</strong> posts on a partner&apos;s behalf.
          Every draft is reviewed and sent by the partner themselves.
        </Block>

        <Block icon={Handshake} title="How referrals & payouts work" testid="trust-block-referrals">
          When a player arrives at Gully through a partner&apos;s link, we mark that partner as their
          referrer (first-touch attribution). If the player later upgrades to Gully Plus, the partner
          earns a share of that revenue. Every wallet line maps to a real Gully transaction — nothing fabricated.
          <br />
          Read the full <Link to="/legal/referral_policy" className="text-emerald-400 hover:underline" data-testid="trust-referral-policy-link">Referral & Commission Policy</Link>.
        </Block>

        <Block icon={Video} title="How player reviews work" testid="trust-block-video-reviews">
          You upload a short clip — usually 10–20 seconds. Gully analyses it for technique cues using AI,
          then writes a focused review with one or two things to work on. That review is in your private chat.
        </Block>

        <Block icon={Brain} title="Why we store your videos">
          So Gully can <em>compare</em> your next clip to your previous one and tell you what&apos;s actually
          improving. Over time, this becomes a personalised cricket development record only you can see.
        </Block>

        <Block icon={Lock} title="Security principles" testid="trust-block-security">
          Player content stays private. Payouts run on an immutable ledger — historical rows are never rewritten.
          Suspicious activity pauses payouts, not partner accounts. Every material change to a policy creates a
          new version; older versions are preserved.
        </Block>

        <Block icon={Trash2} title="Delete anything, anytime">
          From <Link to="/me" className="text-emerald-400 hover:underline">account settings</Link> you can
          export a JSON of everything Gully knows about you, delete just your videos while keeping
          conversations, or delete your account entirely. Account deletions are reversible for 30 days,
          then everything is permanently purged.
        </Block>

        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5" data-testid="trust-legal-links">
          <div className="text-sm text-neutral-300 mb-2">Legal documents</div>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/legal/partner_agreement" className="text-emerald-400 hover:underline" data-testid="trust-link-partner-agreement">Partner Agreement</Link></li>
            <li><Link to="/legal/referral_policy" className="text-emerald-400 hover:underline" data-testid="trust-link-referral-policy">Referral & Commission Policy</Link></li>
            <li><Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-emerald-400 hover:underline">Terms of Service</Link></li>
          </ul>
        </div>

        <div className="pt-4 text-xs text-neutral-500">
          Questions? Reply to any Gully email — a real person will read it.
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Block({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm text-emerald-400 mb-2">
        <Icon size={16} /> <span className="font-medium">{title}</span>
      </div>
      <div className="text-[15px] text-neutral-300 leading-relaxed">{children}</div>
    </section>
  );
}
